import React from 'react'
import { useIntl } from 'react-intl'
import { BasicDialogBody, Dialog } from '../../legacy-design-system/modal'
import { ExclusionFormManager } from './exclusion-form-manager'
import { Link, Text } from '@contrast-security-inc/design-system-typography'
import { css } from '@emotion/css'
import { useContrastMutation } from '../../hooks/use-contrast-mutation'
import { logSuccess, logError } from '../toaster'
import {
  getErrorMsg,
  getExclusionError,
  initExclusionFormFromExclusion,
  isBadRequest,
  isDuplicateRequest,
  isFormInvalid,
  normalizeExclusionForm
} from './shared'
import { HTTPError } from '../../api/fetch/decorate-fetch'
import { FormValidator } from '../../legacy-design-system/form'
import { ExclusionForm } from './exclusion-form'
import { AgentLanguageValue } from '../agent-language'
import { useAppExclusion } from './api/use-application-exclusion'
import {
  UpdateAppExclusionArgs,
  updateApplicationExclusion
} from './api/requests/update-application-exclusion'
import { ExclusionWrapper } from './exclusion-wrapper'
import { BaseApiResponse } from '../../api/shared'

export const testIds = {
  dialog: 'edit-exclusion-dialog',
  saveButton: 'edit-exclusion-button'
}

interface EditExclusionDialogProps {
  organizationUuid: string
  applicationId: string
  exclusionId: string
  applicationName: string
  applicationLanguage: AgentLanguageValue
  isOpen: boolean
  isAssessFeatureEnabled: boolean
  isProtectFeatureEnabled: boolean
  closeDialog: () => void
  successCallback: () => void
}

export function EditExclusionDialog({
  organizationUuid,
  applicationId,
  exclusionId,
  applicationName,
  applicationLanguage,
  isOpen,
  isAssessFeatureEnabled,
  isProtectFeatureEnabled,
  closeDialog,
  successCallback
}: EditExclusionDialogProps) {
  const intl = useIntl()
  const {
    isLoading: isExclusionLoading,
    data: exclusion,
    error: exclusionError
  } = useAppExclusion(organizationUuid, applicationId, exclusionId)

  const { mutate: updateExclusion } = useContrastMutation<
    BaseApiResponse,
    HTTPError,
    UpdateAppExclusionArgs
  >(updateApplicationExclusion)

  if (exclusionError) {
    throw new Error('Failed getting exclusion data')
  }

  if (isExclusionLoading || !exclusion) {
    return <></>
  }

  return (
    <ExclusionFormManager
      exclusion={initExclusionFormFromExclusion(exclusion)}
      organizationUuid={organizationUuid}
      language={applicationLanguage}
      isAssessFeatureEnabled={isAssessFeatureEnabled}
      isProtectFeatureEnabled={isProtectFeatureEnabled}
      exclusionRules={{
        selectedAssesRules: exclusion.assessRules,
        selectedProtectRules: exclusion.protectRules
      }}
    >
      {({
        exclusion: exclusionForm,
        exclusionError: exclusionFormError,
        shouldSuppressEvents,
        commonRules,
        protectOnlyRules,
        assessOnlyRules,
        selectedRules,
        setExclusion,
        setExclusionError,
        setSelectedRules
      }) => (
        <FormValidator value={exclusionForm}>
          {({ getFormFieldProps, formIsValid }) => {
            const descriptionMsg = intl.formatMessage(
              {
                id: 'detail_appExclusions_edit'
              },
              {
                a: (...msg) => (
                  <Link
                    isExternal
                    font="metadata"
                    href={intl.formatMessage({
                      id: 'detail_appExclusions_docLink'
                    })}
                  >
                    {msg}
                  </Link>
                )
              }
            )

            return (
              <Dialog
                testId={testIds.dialog}
                size="large"
                isOpen={isOpen}
                onCancel={closeDialog}
                renderTitle={() => (
                  <>
                    <Text
                      font="bodyStrong"
                      color="neutral100"
                      className={css`
                        margin-right: 5px;
                      `}
                    >
                      {intl.formatMessage({
                        id: 'title_applicationExclusion_editExclusion'
                      })}
                    </Text>
                    <Text font="body" color="neutral60">
                      ({applicationName})
                    </Text>
                  </>
                )}
                renderDescription={() => descriptionMsg}
              >
                <BasicDialogBody
                  disableConfirmButton={isFormInvalid(
                    formIsValid,
                    exclusionForm.exclusionName
                  )}
                  body={
                    <ExclusionForm
                      exclusion={exclusionForm}
                      exclusionError={exclusionFormError}
                      shouldSuppressEvents={shouldSuppressEvents}
                      shouldAllowSuppressEvents={false}
                      commonRules={commonRules}
                      assessOnlyRules={assessOnlyRules}
                      protectOnlyRules={protectOnlyRules}
                      selectedRules={selectedRules}
                      applicationLanguage={applicationLanguage}
                      hasAttackEvent={false}
                      getFormFieldProps={getFormFieldProps}
                      setExclusion={setExclusion}
                      setSelectedRules={setSelectedRules}
                    />
                  }
                  onConfirm={() => {
                    updateExclusion(
                      {
                        organizationUuid,
                        applicationId,
                        exclusionId: exclusion.exceptionId.toString(),
                        exclusion: normalizeExclusionForm(
                          exclusionForm,
                          selectedRules
                        )
                      },
                      {
                        onSuccess: ({ messages }) => {
                          setExclusionError({})
                          logSuccess(messages[0])
                          closeDialog()
                          successCallback()
                        },
                        onError: ({ data: { messages, errors }, status }) => {
                          logError(getErrorMsg(messages))

                          if (
                            isBadRequest(status) &&
                            Array.isArray(errors) &&
                            errors.length > 0
                          ) {
                            setExclusionError(getExclusionError(errors))
                          } else if (isDuplicateRequest(status)) {
                            closeDialog()
                          }
                        }
                      }
                    )
                  }}
                  onCancel={closeDialog}
                  confirmButtonText={
                    <Text testId={testIds.saveButton} font="body">
                      {intl.formatMessage({
                        id: 'action_save'
                      })}
                    </Text>
                  }
                  cancelButtonText={
                    <Text font="body">
                      {intl.formatMessage({
                        id: 'action_cancel'
                      })}
                    </Text>
                  }
                />
              </Dialog>
            )
          }}
        </FormValidator>
      )}
    </ExclusionFormManager>
  )
}

interface EditExclusionDialogWrapperProps {
  organizationUuid: string
  applicationId: string
  exclusionId: string
  isOpen: boolean
  isRASPEnabled: boolean
  closeDialog: () => void
  successCallback: () => void
}

export function EditExclusionDialogWrapper({
  organizationUuid,
  applicationId,
  exclusionId,
  isOpen,
  isRASPEnabled,
  closeDialog,
  successCallback
}: EditExclusionDialogWrapperProps) {
  return (
    <ExclusionWrapper
      organizationUuid={organizationUuid}
      applicationId={applicationId}
    >
      {({
        application,
        isFeatureEnabledForAssess,
        isFeatureEnabledForProtect
      }) => (
        <EditExclusionDialog
          organizationUuid={organizationUuid}
          applicationId={applicationId}
          exclusionId={exclusionId}
          applicationName={application.name}
          applicationLanguage={application.language}
          isOpen={isOpen}
          isAssessFeatureEnabled={isFeatureEnabledForAssess}
          isProtectFeatureEnabled={isFeatureEnabledForProtect && isRASPEnabled}
          closeDialog={closeDialog}
          successCallback={successCallback}
        />
      )}
    </ExclusionWrapper>
  )
}
